/**
 * The field catalogue — all 29 product fields in the prototype's default order
 * (spec R36). Held as a module constant: the later API can supply the same
 * shape through the repository without changing a consumer.
 *
 * `supportsLabel` mirrors the prototype's set of label-prefixed renderers and
 * `defaultDisplayLabel` its starting values; `supportsAlignment` and
 * `supportsReorder` are every field except Primary Image, which is the photo
 * background rather than a block. The image still gets a properties panel —
 * every enabled field does (spec R46, amended) — its panel offers Image display
 * instead of label and alignment.
 */

import { FieldDefinition, FieldId, FieldConfiguration, ImageFit } from '../types';
import { DEFAULT_CTA_CONFIGURATION, DEFAULT_DESCRIPTION_STYLE } from './configuration.config';
import { PROPERTY_SECTIONS } from './propertySections.config';

const plain = (
  id: FieldId,
  name: string,
  defaultEnabled = false
): FieldDefinition => ({
  id,
  name,
  defaultEnabled,
  supportsLabel: false,
  defaultDisplayLabel: '',
  supportsAlignment: true,
  supportsReorder: true,
});

const labelled = (
  id: FieldId,
  name: string,
  defaultDisplayLabel: string,
  defaultEnabled = false
): FieldDefinition => ({
  id,
  name,
  defaultEnabled,
  supportsLabel: true,
  defaultDisplayLabel,
  supportsAlignment: true,
  supportsReorder: true,
});

export const PRODUCT_FIELDS: FieldDefinition[] = [
  // Visual — the image is the photo background, so it has neither a display
  // label nor an alignment; its properties panel offers Image display instead.
  {
    id: 'image',
    name: 'Primary Image',
    defaultEnabled: true,
    supportsLabel: false,
    defaultDisplayLabel: '',
    supportsAlignment: false,
    /* …and no reorder: it has no placement, so where it sits in `fieldOrder`
       is invisible. See the field's own note in `FieldDefinition`. */
    supportsReorder: false,
  },
  // Identity
  labelled('sku', 'SKU / Part Number', 'SKU'),
  labelled('title', 'Product Title / Name', '', true),
  plain('brand', 'Brand / Manufacturer'),
  labelled('model', 'Model Number', 'Model'),
  plain('desc', 'Short Description'),
  // Pricing. The "Show UOM on prices" row used to sit here: a field that rendered no
  // block, whose only effect was a card-wide suffix on every price. It was removed on
  // 2026-09-18 (14021) when UOM became a per-element setting in the Pricing section,
  // which is where a merchandiser looks for it and which lets one price carry the unit
  // while another does not.
  labelled('listPrice', 'List Price', '', true),
  plain('salePrice', 'Sale Price'),
  labelled('savingsAmt', 'Savings Amount', 'Save'),
  labelled('savingsPct', 'Savings Percentage', 'Save'),
  plain('pricePerUnit', 'Price Per Unit'),
  labelled('currency', 'Currency', 'Currency'),
  // Availability
  plain('stock', 'Stock Status'),
  labelled('leadTime', 'Lead Time', 'Lead time'),
  labelled('qtyAvail', 'Quantity In Stock', 'In stock'),
  plain('discontinued', 'Discontinued Flag'),
  // Ratings & reviews
  plain('starRating', 'Star Rating'),
  plain('reviewCount', 'Review Count'),
  // B2B
  labelled('moq', 'Min Order Qty (MOQ)', 'MOQ'),
  labelled('qtyIncrement', 'Order Qty Increment', 'Qty step'),
  plain('preferred', 'Preferred / Approved Flag'),
  // Merchandising / badges
  plain('badge', 'Product Badge / Label'),
  plain('promoMsg', 'Promotional Message'),
  plain('newArrival', 'New Arrival Flag'),
  plain('bestSeller', 'Best Seller Rank'),
  plain('sponsored', 'Sponsored / Featured'),
  // Actions / CTA
  plain('cta', 'Primary CTA Button', true),
  plain('qtySelector', 'Quantity Selector'),
];

/** Rail order for a brand-new card. */
export const DEFAULT_FIELD_ORDER: FieldId[] = PRODUCT_FIELDS.map((field) => field.id);

/** Primary Image, Product Title / Name, List Price, Primary CTA Button (R36). */
export const DEFAULT_ENABLED_FIELD_IDS: FieldId[] = PRODUCT_FIELDS.filter(
  (field) => field.defaultEnabled
).map((field) => field.id);

const FIELDS_BY_ID: Map<FieldId, FieldDefinition> = new Map(
  PRODUCT_FIELDS.map((field) => [field.id, field])
);

/** The whitelist every untrusted read goes through (threat model, abuse case 1). */
export const isKnownFieldId = (candidate: unknown): candidate is FieldId =>
  typeof candidate === 'string' && FIELDS_BY_ID.has(candidate as FieldId);

export const getFieldDefinition = (fieldId: FieldId): FieldDefinition => {
  const definition = FIELDS_BY_ID.get(fieldId);
  if (!definition) {
    // Unreachable for a FieldId; keeps the return type honest without a cast.
    throw new Error(`Unknown product card field: ${String(fieldId)}`);
  }
  return definition;
};

/**
 * The Primary Image is the photo background, not a block — it never gets a
 * placement record.
 */
export const IMAGE_FIELD_ID: FieldId = 'image';

/** The image fills its region proportionally until the user says otherwise. */
export const DEFAULT_IMAGE_FIT: ImageFit = 'fit';

export const IMAGE_FITS: ImageFit[] = ['fit', 'fill', 'stretch'];

/** The clamp every untrusted read of `imageFit` goes through (threat model). */
export const isImageFit = (candidate: unknown): candidate is ImageFit =>
  IMAGE_FITS.some((fit) => fit === candidate);

/** The CTA stays last in its region as other fields are enabled (spec R39). */
export const CTA_FIELD_ID: FieldId = 'cta';

/** Short Description — the one field with a Description style setting (R71). */
export const DESC_FIELD_ID: FieldId = 'desc';

/**
 * Fields that contribute no block to any region.
 *
 * The Primary Image *is* the photo background, so it contributes no block of its own.
 * Held as data so the invariant lives in one place — `normalizeCard` gives it no
 * placement, so no region can render an empty shell for it and no preview component
 * branches on a field id. (The UOM field was the other member until 2026-09-18; it is
 * gone, and its suffix is now a per-element Pricing setting — 14021.)
 */
export const BLOCKLESS_FIELD_IDS: FieldId[] = [IMAGE_FIELD_ID];

export const rendersBlock = (fieldId: FieldId): boolean =>
  !BLOCKLESS_FIELD_IDS.includes(fieldId);

/**
 * Blocks whose content is sized as a fraction of the card's content width rather
 * than by its own text, so the block has to fill its row for that fraction to mean
 * anything — the CTA button's width options (spec R70).
 */
export const STRETCH_BLOCK_FIELD_IDS: FieldId[] = [CTA_FIELD_ID];

export const isStretchBlock = (fieldId: FieldId): boolean =>
  STRETCH_BLOCK_FIELD_IDS.includes(fieldId);

/**
 * A newly enabled field's configuration: alignment always starts Left (spec R44), and
 * each field type's own setting starts on the default its requirement states —
 * Image display Fit (R46), Description style "Clamp 2 lines" (R71), the CTA's
 * options (R70). Custom CSS starts absent rather than empty (R67).
 *
 * The two price booleans start written out as `false` rather than absent (operator,
 * 2026-09-28), so a new field's stored configuration lists every option its panel
 * offers instead of leaving the reader to infer that a missing key means off. Which
 * ones a field gets is read from the same table the panel renders from, so
 * `savingsPct` — Strikethrough but no Pricing — correctly gets `strikethrough` and
 * not `showUom`.
 *
 * MUST AGREE WITH `state/normalize.ts`. `encodeCard` falls back to this function for
 * an enabled field with no stored entry, while every other path goes through
 * `normalizeConfiguration` — so if the two disagree about which keys exist, the round
 * trip stops being a fixpoint and `contract.test.ts` says so.
 */
export const getDefaultFieldConfiguration = (fieldId: FieldId): FieldConfiguration => ({
  displayLabel: getFieldDefinition(fieldId).defaultDisplayLabel,
  alignment: 'left',
  ...(fieldId === IMAGE_FIELD_ID ? { imageFit: DEFAULT_IMAGE_FIT } : {}),
  ...(fieldId === DESC_FIELD_ID ? { descStyle: DEFAULT_DESCRIPTION_STYLE } : {}),
  ...(fieldId === CTA_FIELD_ID ? { cta: DEFAULT_CTA_CONFIGURATION } : {}),
  ...(PROPERTY_SECTIONS[fieldId].includes('strikethrough') ? { strikethrough: false } : {}),
  ...(PROPERTY_SECTIONS[fieldId].includes('pricing') ? { showUom: false } : {}),
});
